// src/components/editModalFunctions.ts
// Funktioner exklusiva för EditModal – hook-fria och med alla beroenden injicerade via argument.

import {
    doc,
    getDoc,
    runTransaction,
    updateDoc,
    arrayUnion,
    getDocs,
    collection,
    serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import type { Timestamp } from "firebase/firestore";

// === Typer du redan har i projektet – justera importvägar om du vill ha strikt typning ===

export const LOCK_STALE_MS = 5 * 60 * 1000;

export const PHOTO_LABELS: Record<string, string> = {
    keyboard: "Keyboard",
    screen: "Screen",
    underside: "Underside",
    topside: "Topside",
};




export const toKey = (s: string) => s.trim().toLocaleLowerCase("sv");

export const toMillis = (d: any): number | null => {
    if (!d) return null;
    if (typeof d?.toDate === "function") return d.toDate().getTime();
    if (typeof d === "string") {
        const t = Date.parse(d);
        return Number.isNaN(t) ? null : t;
    }
    if (d instanceof Date) return d.getTime();
    if (typeof d?.seconds === "number") return d.seconds * 1000;
    return null;
};

export const normalizeSerialKey = (s: string): string =>
    (s || "").trim().toUpperCase().replace(/[\s\-_.:/\\]/g, "");

export function splitSerialParts(s: string) {
    const [raw, suffix] = String(s || "").split("*");
    const base = (raw || "").trim();
    const visit = Math.max(1, Number.parseInt(suffix || "", 10) || 1);
    return { base, visit };
}

export function buildDisplaySerial(rawSerial: string, visit: number): string {
    return visit > 1 ? `${rawSerial}*${visit}` : rawSerial;
}

export async function reallocateSerialOnEdit(
    itemId: string,
    _prevBaseKey: string | null,
    nextSerial: string,
    actor: string | null = null
): Promise<{ changed: boolean; serialBaseKey: string; serialVisit: number; displaySerial: string; }> {
    const { base: nextBaseRaw } = splitSerialParts(String(nextSerial || "").trim());
    const nextBaseKey = normalizeSerialKey(nextBaseRaw || "");

    if (!nextBaseKey) throw new Error("[reallocateSerialOnEdit] nextBaseKey saknas");

    const itemRef = doc(db, "itInventory", itemId);
    const newRef = doc(db, "serialIndex", nextBaseKey);

    return await runTransaction(db, async (tx) => {
        const itemSnap = await tx.get(itemRef);
        if (!itemSnap.exists()) throw new Error("[reallocateSerialOnEdit] item saknas");

        // Utgå alltid från objektets aktuella DB-state. Det gör en transaktionsretry
        // eller en andra parallell save idempotent även om UI:t fortfarande bär den
        // ursprungliga basnyckeln.
        const item = itemSnap.data() as any;
        const currentBaseKey = normalizeSerialKey(
            String(item.serialBaseKey || item.serialBase || splitSerialParts(String(item.serial || "")).base)
        );
        const currentVisit = Number(item.serialVisit || splitSerialParts(String(item.serial || "")).visit || 1);

        if (currentBaseKey === nextBaseKey) {
            return {
                changed: false,
                serialBaseKey: nextBaseKey,
                serialVisit: currentVisit,
                displaySerial: buildDisplaySerial(nextBaseRaw, currentVisit),
            };
        }

        const oldRef = currentBaseKey ? doc(db, "serialIndex", currentBaseKey) : null;
        const oldSnap = oldRef ? await tx.get(oldRef) : null;
        const newSnap = await tx.get(newRef);

        const newIndex = newSnap.exists() ? newSnap.data() as any : {};
        let nextVisit = Number(newIndex.visits || 0) + 1;
        let claimRef;
        // Claims är den permanenta sanningskällan för redan använda visits. Hoppa
        // över claimade nummer även om ett äldre index skulle vara inkonsekvent.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            claimRef = doc(db, "serialIndex", nextBaseKey, "claims", String(nextVisit));
            const claimSnap = await tx.get(claimRef);
            if (!claimSnap.exists()) break;
            nextVisit++;
        }

        const nowServer = serverTimestamp();

        if (oldRef && oldSnap?.exists()) {
            const oldIndex = oldSnap.data() as any;
            tx.update(oldRef, {
                active: Math.max(0, Number(oldIndex.active || 0) - 1),
                lastSeen: nowServer,
                lastItemId: itemId,
            });
        }

        tx.set(claimRef, { itemId, at: nowServer, by: actor });
        tx.set(newRef, {
            lastVisit: nextVisit,
            visits: nextVisit,
            active: Number(newIndex.active || 0) + 1,
            lastSeen: nowServer,
            lastItemId: itemId,
        }, { merge: true });

        const displaySerial = buildDisplaySerial(nextBaseRaw, nextVisit);
        tx.update(itemRef, {
            serial: displaySerial,
            serialBase: nextBaseRaw,
            serialBaseKey: nextBaseKey,
            serialVisit: nextVisit,
            updatedAt: nowServer,
        });
        return { changed: true, serialBaseKey: nextBaseKey, serialVisit: nextVisit, displaySerial };
    });
}

export async function assertValidProductTypeOrExplain(productTypeId?: string | null, productTypeLabel?: string | null): Promise<boolean> {
    // Implementationen från App.tsx flyttas hit
    if (!productTypeId || !String(productTypeId).trim()) {
        alert("Produkttyp saknar ID...");
        return false;
    }
    const ref = doc(db, "productTypes", String(productTypeId));
    const snap = await getDoc(ref);
    if (!snap.exists()) {
        alert(
            `Produkttypen '${productTypeLabel || productTypeId}' med ID '${productTypeId}' finns inte. ` +
            "Välj en giltig produkttyp i redigeringsrutan och försök igen."
        );
        return false;
    }
    const data = snap.data() as any;
    const active = data?.active !== false;
    const weight = Number(data?.medianWeightKg ?? 0);
    const co2 = Number(data?.co2PerUnitKg ?? 0);
    if (!active || weight <= 0 || co2 <= 0) {
        const reasons = [
            !active ? "inaktiv" : "",
            weight <= 0 ? "saknar medianvikt" : "",
            co2 <= 0 ? "saknar CO₂-faktor" : "",
        ].filter(Boolean).join(", ");
        alert(
            `Produkttypen '${data?.label ?? productTypeLabel ?? productTypeId}' kan inte användas (${reasons}). ` +
            "Välj en annan produkttyp eller komplettera produkttypen i administrationen."
        );
        return false;
    }
    return true;
}

// === SerialIndex: guardrail/telemetri-helper ===
export function warnIfInvariantBroken(ctx: {
    where: string;            // t.ex. "CREATE", "EDIT:new", "EDIT:old", "SOFT-DELETE", "UNDO-DELETE"
    baseKey: string;
    visits?: number | null;
    active?: number | null;
}) {
    const v = typeof ctx.visits === "number" ? ctx.visits : null;
    const a = typeof ctx.active === "number" ? ctx.active : null;

    if (a !== null && a < 0) {
        console.warn("[serialIndex] invariant: active < 0", ctx);
    }
    if (v !== null && a !== null && v < a) {
        console.warn("[serialIndex] invariant: visits < active", ctx);
    }
}
export type FirestoreDB = any;

export interface EditFormMinimal {
    deletePending?: boolean;
    completed?: boolean;

    orderNumber?: string;
    manufacturer?: string;
    model?: string;
    productType?: string;
    productTypeId?: string;

    chargerIncluded?: boolean;
    damageNotes?: string;

    reuse?: boolean;
    resold?: boolean;
    scrap?: boolean;
    grade?: "A" | "B" | "C" | "D" | "E" | "";

    salesBatchId?: string | null;
    salesBatchName?: string | null;
    salesKolliId?: string | null;
    salesKolliName?: string | null;
    salesCpu?: string | null;
    salesStorage?: string | null;
    salesRam?: string | null;

    serial?: string;
}

export interface SaveEditArgs {
    // Firestore
    db: FirestoreDB;
    editId: string;

    // Form-state (kan bytas till din EditFormState-typ)
    editForm: EditFormMinimal;

    // Behörighet/användare
    editIsReadOnly: boolean;
    currentUserString: () => string | null;

    // Lås/TTL
    toMillis: (d: any) => number | null;
    LOCK_STALE_MS: number;

    // Serial-hjälpare – hämtas från din centraliserade serialIndex.ts
    splitSerialParts: (s: string) => { base: string; visit: number };
    normalizeSerialKey: (s: string) => string;
    buildDisplaySerial: (base: string, visit: number) => string;

    // Orkestrering: App har redan kört reallocation när det behövs
    serialAlreadyReallocated: boolean; // t.ex. !!lastSerialAllocRef.current?.changed

    // UI/side-effects efter lyckad transaktion
    refreshHomeList: () => Promise<void>;
    setIsEditOpen: (v: boolean) => void;
    setEditId: (v: string | null) => void;
    setEditForm: (updater: (prev: any) => any) => void;

    // Feedback/logg
    alertFn?: (msg: string) => void;
    consoleError?: (e: any) => void;
}

export interface AtomicSaveEditArgs extends Omit<SaveEditArgs, "serialAlreadyReallocated"> {
    maxClaimRetries?: number;
}

export type AtomicSaveEditResult = {
    changed: boolean;
    serialBaseKey: string;
    serialVisit: number;
    displaySerial: string;
};

function editPayload(editForm: EditFormMinimal, actor: string | null, nowIso: string) {
    const resoldPayload = editForm.resold ? {
        salesBatchId: editForm.salesBatchId || "",
        salesBatchName: editForm.salesBatchName || "",
        salesKolliId: editForm.salesKolliId || "",
        salesKolliName: editForm.salesKolliName || "",
        salesCpu: editForm.salesCpu || "",
        salesStorage: editForm.salesStorage || "",
        salesRam: editForm.salesRam || "",
    } : {
        salesBatchId: "",
        salesBatchName: "",
        salesKolliId: "",
        salesKolliName: "",
        salesCpu: "",
        salesStorage: "",
        salesRam: "",
    };

    return {
        orderNumber: editForm.orderNumber || "",
        manufacturer: editForm.manufacturer || "",
        model: editForm.model || "",
        productType: editForm.productType || "",
        productTypeId: editForm.productTypeId || "",
        chargerIncluded: !!editForm.chargerIncluded,
        damageNotes: editForm.damageNotes ?? "",
        reuse: !!editForm.reuse,
        resold: !!editForm.resold,
        scrap: !!editForm.scrap,
        grade: editForm.grade ?? "",
        ...resoldPayload,
        updatedAt: serverTimestamp(),
        auditLog: arrayUnion({ action: "updated", by: actor, at: nowIso }),
    };
}

function assertCurrentItemEditable(cur: any, actor: string | null, toMillisFn: (d: any) => number | null, staleMs: number) {
    if (cur.invoiceReportId) throw new Error("Enheten är fakturerad och permalåst.");
    if (cur.completed) throw new Error("Enheten är markerad som färdig och kan inte ändras.");
    if (cur.deletePending) throw new Error("Enheten är markerad för radering och kan inte ändras.");
    if (cur.markedForInvoice === true && !cur.invoiceReportId) {
        throw new Error("Detta objekt är markerat för fakturering och kan inte redigeras nu.");
    }

    const heldByOther = !!cur.lockedBy && cur.lockedBy !== actor;
    const lockedAtMs = toMillisFn(cur.lockedAt);
    const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > staleMs;
    if (heldByOther && !isStale) throw new Error(`Posten redigeras av ${cur.lockedBy}.`);
}

/**
 * Sparar vanliga editfält, audit och eventuell serienummeromallokering i samma
 * Firestore-transaktion. En legitim claim-kollision får ett fåtal kontrollerade
 * omförsök; verkliga behörighets- och statusfel skickas vidare direkt.
 */
export async function saveEditAtomicallyWithSerial({
    db, editId, editForm, editIsReadOnly, currentUserString,
    toMillis, LOCK_STALE_MS, splitSerialParts, normalizeSerialKey, buildDisplaySerial,
    refreshHomeList, setEditForm,
    alertFn = (m) => alert(m),
    maxClaimRetries = 3,
}: AtomicSaveEditArgs): Promise<AtomicSaveEditResult> {
    if (!editId) throw new Error("Post-ID saknas.");
    if (editForm.deletePending) throw new Error("Denna enhet är markerad för radering och kan inte editeras.");
    if (editIsReadOnly) throw new Error("Denna enhet är fakturerad och kan inte editeras.");
    if (editForm.completed) throw new Error("Denna enhet är markerad som färdig och kan inte editeras.");

    const selectedCount = Number(!!editForm.reuse) + Number(!!editForm.resold) + Number(!!editForm.scrap);
    if (selectedCount > 1) throw new Error("Du kan inte spara med mer än ett alternativ markerat.");

    const { base: nextBaseRaw } = splitSerialParts(String(editForm.serial || "").trim());
    const nextBaseKey = normalizeSerialKey(nextBaseRaw);
    if (!nextBaseKey) throw new Error("Serienummer saknas.");

    const actor = currentUserString();
    const itemRef = doc(db, "itInventory", editId);
    const newRef = doc(db, "serialIndex", nextBaseKey);
    const nowIso = new Date().toISOString();
    let payloadForUi: Record<string, any> = {};

    for (let attempt = 0; attempt < Math.max(1, maxClaimRetries); attempt++) {
        let attemptedVisit: number | null = null;
        try {
            const result = await runTransaction(db, async (tx) => {
                const itemSnap = await tx.get(itemRef);
                if (!itemSnap.exists()) throw new Error("Posten finns inte längre.");
                const cur = itemSnap.data() as any;
                assertCurrentItemEditable(cur, actor, toMillis, LOCK_STALE_MS);

                const currentBaseKey = normalizeSerialKey(
                    String(cur.serialBaseKey || cur.serialBase || splitSerialParts(String(cur.serial || "")).base)
                );
                const currentVisit = Number(cur.serialVisit || splitSerialParts(String(cur.serial || "")).visit || 1);
                const basePayload = editPayload(editForm, actor, nowIso);

                if (currentBaseKey === nextBaseKey) {
                    const displaySerial = buildDisplaySerial(nextBaseRaw, currentVisit);
                    payloadForUi = {
                        ...basePayload,
                        serial: displaySerial,
                        serialBase: nextBaseRaw,
                        serialBaseKey: nextBaseKey,
                        serialVisit: currentVisit,
                    };
                    tx.update(itemRef, payloadForUi);
                    return { changed: false, serialBaseKey: nextBaseKey, serialVisit: currentVisit, displaySerial };
                }

                const oldRef = currentBaseKey ? doc(db, "serialIndex", currentBaseKey) : null;
                const oldSnap = oldRef ? await tx.get(oldRef) : null;
                const newSnap = await tx.get(newRef);
                const newIndex = newSnap.exists() ? newSnap.data() as any : {};
                let nextVisit = Number(newIndex.visits || 0) + 1;
                let claimRef;
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    claimRef = doc(db, "serialIndex", nextBaseKey, "claims", String(nextVisit));
                    const claimSnap = await tx.get(claimRef);
                    if (!claimSnap.exists()) break;
                    nextVisit++;
                }
                attemptedVisit = nextVisit;

                const nowServer = serverTimestamp();
                if (oldRef && oldSnap?.exists()) {
                    const oldIndex = oldSnap.data() as any;
                    tx.update(oldRef, {
                        active: Math.max(0, Number(oldIndex.active || 0) - 1),
                        lastSeen: nowServer,
                        lastItemId: editId,
                    });
                }

                tx.set(claimRef, { itemId: editId, at: nowServer, by: actor });
                tx.set(newRef, {
                    lastVisit: nextVisit,
                    visits: nextVisit,
                    active: Number(newIndex.active || 0) + 1,
                    lastSeen: nowServer,
                    lastItemId: editId,
                }, { merge: true });

                const displaySerial = buildDisplaySerial(nextBaseRaw, nextVisit);
                payloadForUi = {
                    ...basePayload,
                    serial: displaySerial,
                    serialBase: nextBaseRaw,
                    serialBaseKey: nextBaseKey,
                    serialVisit: nextVisit,
                };
                tx.update(itemRef, payloadForUi);
                return { changed: true, serialBaseKey: nextBaseKey, serialVisit: nextVisit, displaySerial };
            });

            setEditForm((prev: any) => {
                const uiPayload = { ...payloadForUi };
                delete uiPayload.updatedAt;
                delete uiPayload.auditLog;
                return {
                    ...prev,
                    ...uiPayload,
                    updatedAt: nowIso,
                    auditLog: [
                        ...(Array.isArray(prev.auditLog) ? prev.auditLog : []),
                        { action: "updated", by: actor, at: nowIso },
                    ],
                };
            });
            alertFn("Enheten uppdaterad.");
            await refreshHomeList();
            return result;
        } catch (err: any) {
            const mayBeClaimCollision = err?.code === "permission-denied"
                && attemptedVisit !== null
                && attempt + 1 < Math.max(1, maxClaimRetries);
            if (!mayBeClaimCollision) throw err;

            try {
                const claimRef = doc(db, "serialIndex", nextBaseKey, "claims", String(attemptedVisit));
                const [latestItem, claimedVisit] = await Promise.all([getDoc(itemRef), getDoc(claimRef)]);
                const latest = latestItem.exists() ? latestItem.data() as any : null;
                const latestBaseKey = latest
                    ? normalizeSerialKey(String(latest.serialBaseKey || latest.serialBase || splitSerialParts(String(latest.serial || "")).base))
                    : null;
                const claimedByOther = claimedVisit.exists() && (claimedVisit.data() as any)?.itemId !== editId;
                if (latest && latestBaseKey !== nextBaseKey && claimedByOther) continue;
            } catch { /* Det ursprungliga felet är mer relevant än en misslyckad kollisionskontroll. */ }
            throw err;
        }
    }

    throw new Error("Kunde inte reservera ett unikt besöksnummer efter flera samtidiga försök.");
}

/**
 * saveEdit — sparar redigerade fält från EditModal.
 * - App ansvarar för delete-/lås-guards, watchers och ev. reallocation innan anropet.
 * - Här gör vi en transaktion som validerar mot aktuell DB-state och uppdaterar fälten.
 */
export async function saveEdit({
    db, editId, editForm, editIsReadOnly, currentUserString,
    toMillis, LOCK_STALE_MS,
    splitSerialParts, normalizeSerialKey, buildDisplaySerial,
    serialAlreadyReallocated,
    refreshHomeList, setIsEditOpen, setEditId, setEditForm,
    alertFn = (m) => alert(m),
    consoleError = console.error,
}: SaveEditArgs): Promise<void> {
    if (!editId) return;

    // Grundspärrar (samma som i App – bättre att dubbelskydda här)
    if (editForm.deletePending) { alertFn("Denna enhet är markerad för radering och kan inte editeras."); return; }
    if (editIsReadOnly) { alertFn("Denna enhet är fakturerad och kan inte editeras."); return; }
    if (editForm.completed) { alertFn("Denna enhet är markerad som färdig och kan inte editeras."); return; }

    const selectedCount =
        Number(!!editForm.reuse) + Number(!!editForm.resold) + Number(!!editForm.scrap);
    if (selectedCount > 1) { alertFn("Du kan inte spara med mer än ett alternativ markerat."); return; }

    try {
        // Pre-spärr: markerad för fakturering (samma som du har i App)
        try {
            const preRef = doc(db, "itInventory", editId);
            const preSnap = await getDoc(preRef);
            const pre = preSnap.exists() ? (preSnap.data() as any) : null;
            if (pre?.markedForInvoice === true && !pre?.invoiceReportId) {
                alertFn("Detta objekt är markerat för fakturering och kan inte redigeras nu.");
                return;
            }
        } catch { /* Ignorera läsfel här */ }

        const nowIso = new Date().toISOString();
        let payload: Record<string, any> = {};

        await runTransaction(db, async (tx) => {
            const itemRef = doc(db, "itInventory", editId);
            const snap = await tx.get(itemRef);
            if (!snap.exists()) throw new Error("Posten finns inte längre.");
            const cur = snap.data() as any;

            // Defense-in-depth mot aktuell DB-state
            if (cur.invoiceReportId) throw new Error("Enheten är fakturerad och permalåst.");
            if (cur.completed) throw new Error("Enheten är markerad som färdig och kan inte ändras.");
            if (cur.markedForInvoice === true && !cur.invoiceReportId) {
                throw new Error("Detta objekt är markerat för fakturering och kan inte redigeras nu.");
            }

            const heldByOther = !!cur.lockedBy && cur.lockedBy !== currentUserString();
            const lockedAtMs = toMillis(cur.lockedAt);
            const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
            if (heldByOther && !isStale) {
                throw new Error(`Posten redigeras av ${cur.lockedBy}.`);
            }

            // --- Serial-patch ---
            // App har eventuellt redan kört reallocation mot serialIndex.
            // Om så är fallet ska vi INTE skriva serialfält här.
            let serialPatch: Record<string, any> = {};
            if (!serialAlreadyReallocated) {
                const { visit: oldVisit } = splitSerialParts(String(cur.serial || ""));
                const { base: inputBase } = splitSerialParts(String(editForm.serial || ""));
                if (!inputBase) throw new Error("Serienummer saknas.");

                const serialBase = inputBase.trim();
                const serialBaseKey = normalizeSerialKey(inputBase);
                const serialVisit = Number(cur.serialVisit || oldVisit || 1);
                const serial = buildDisplaySerial(serialBase, serialVisit);

                serialPatch = { serial, serialBase, serialBaseKey, serialVisit };
            }

            // --- Payload ---
            const resoldPayload = editForm.resold ? {
                salesBatchId: editForm.salesBatchId || "",
                salesBatchName: editForm.salesBatchName || "",
                salesKolliId: editForm.salesKolliId || "",
                salesKolliName: editForm.salesKolliName || "",
                salesCpu: editForm.salesCpu || "",
                salesStorage: editForm.salesStorage || "",
                salesRam: editForm.salesRam || "",
            } : {
                salesBatchId: "",
                salesBatchName: "",
                salesKolliId: "",
                salesKolliName: "",
                salesCpu: "",
                salesStorage: "",
                salesRam: "",
            };

            payload = {
                // Formfält
                orderNumber: editForm.orderNumber || "",
                manufacturer: editForm.manufacturer || "",
                model: editForm.model || "",
                productType: editForm.productType || "",
                productTypeId: editForm.productTypeId || "",
                chargerIncluded: !!editForm.chargerIncluded,
                damageNotes: editForm.damageNotes ?? "",

                // Status + grade
                reuse: !!editForm.reuse,
                resold: !!editForm.resold,
                scrap: !!editForm.scrap,
                grade: editForm.grade ?? "",

                ...resoldPayload,

                // Serialfält (endast om vi inte redan reallocated)
                ...serialPatch,

                // Metadata
                updatedAt: serverTimestamp(),
                auditLog: arrayUnion({ action: "updated", by: currentUserString(), at: nowIso }),
            };

            tx.update(itemRef, payload);
        });

        // Synka lokalt state så att "Spara"-knappen deaktiveras korrekt
        setEditForm((prev: any) => {
            const newAuditLog = [
                ...(Array.isArray(prev.auditLog) ? prev.auditLog : []),
                { action: "updated", by: currentUserString(), at: nowIso }
            ];
            // Ta bort server-specifika värden från payload innan vi uppdaterar lokalt state
            delete payload.updatedAt;
            delete payload.auditLog;

            return { ...prev, ...payload, updatedAt: nowIso, auditLog: newAuditLog };
        });

        alertFn("Enheten uppdaterad.");
        // setIsEditOpen(false); // Commented out to keep modal open after save
        // setEditId(null); // Commented out to keep modal open after save
        await refreshHomeList();
    } catch (err: any) {
        consoleError(err);
        alertFn("Kunde inte spara ändringar: " + (err?.message || err));
    }
}
export default { saveEdit };
